import { type ThemeColors } from "../lib/theme";
import type { Ref } from "react";
import type { JSX } from "react/jsx-dev-runtime";

export interface ArticleCardProps {
  article: {
    id: number;
    title: string;
    description: string | null;
  };
  ref?: Ref<HTMLDivElement>;
  theme: ThemeColors;
  className?: string;
  testId?: string;
  buttons?: JSX.Element | null;
  variant?: "default" | "start" | "target";
  onInfoClick?: () => void;
}

export function ArticleCard({
  article,
  ref,
  theme,
  className = "",
  testId,
  buttons = null,
  variant = "default",
  onInfoClick,
}: ArticleCardProps) {
  const isDark = theme.bg.includes("zinc") || theme.bg.includes("black");

  const isClickable = article.description && onInfoClick;

  const getVariantStyles = () => {
    const cursorClass = isClickable ? "cursor-pointer" : "cursor-default";
    if (variant === "start") {
      return isDark
        ? `bg-zinc-900/50 border-zinc-700 hover:border-zinc-600 ${cursorClass}`
        : `bg-gray-100 border-gray-300 hover:border-gray-400 ${cursorClass}`;
    }
    return `${theme.card} ${theme.cardBorder}  ${theme.cardHover} ${cursorClass}`;
  };

  const handleCardClick = () => {
    if (article.description && onInfoClick) {
      onInfoClick();
    }
  };

  return (
    <div
      ref={ref}
      data-testid={testId}
      onClick={handleCardClick}
      className={`group relative p-4 rounded-xl border transition-[border-color,background-color,box-shadow,opacity] duration-200 ${getVariantStyles()} ${className}`}
    >
      <div className="flex items-start gap-4">
        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3
            className={`font-semibold truncate ${isDark ? "text-white" : "text-gray-900"}`}
          >
            {article.title}
          </h3>
          {article.description && (
            <p
              className={`text-sm mt-1 line-clamp-2 ${isDark ? "text-zinc-500" : "text-gray-400"}`}
            >
              {article.description}
            </p>
          )}
        </div>

        {/* Info Button - only interactive element */}
        {article.description && onInfoClick && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onInfoClick();
            }}
            className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center cursor-pointer transition-colors ${
              isDark
                ? "text-zinc-600 hover:text-zinc-400 hover:bg-zinc-900"
                : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            }`}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Bottom Buttons */}
      {buttons && (
        <div
          onClick={(e) => e.stopPropagation()}
          className={`flex items-center gap-2 mt-4 pt-4 border-t ${isDark ? "border-zinc-800" : "border-gray-200"}`}
        >
          {buttons}
        </div>
      )}
    </div>
  );
}
